import type { HistoryData } from '@poseidon/components/ContactDetail';
import { type RouterOutputs } from '@poseidon/api/types';

/** One page of the `history.contact` query, as tRPC returns it. */
export type TrpcHistoryData = NonNullable<RouterOutputs['history']['contact']>['data'];

/**
 * Maps tRPC history data to the pure HistoryData type for ContactInteractSectionPure (and the Graph view's
 * history column, which maps the older pages it loads itself).
 */
export function mapHistoryData(trpcHistory: TrpcHistoryData | undefined): HistoryData {
  if (!trpcHistory) return [];
  return trpcHistory.map((item) => ({
    interaction: {
      id: parseInt(item.interaction.id, 10) || 0,
      odooInteractionId: item.interaction.id, // UUID for retry attachment
      interactionAction: item.interaction.interactionAction,
      userId: item.interaction.userId,
      contactId: '',
      teamId: '',
      interactionType: item.interaction.interactionAction,
      createdAt: item.interaction.date,
      date: item.interaction.date,
      updatedOn: item.interaction.updatedOn,
      subject: item.interaction.subject,
      body: item.interaction.body,
      from: item.interaction.from,
      to: item.interaction.to,
      dataUrl: item.interaction.data_url,
      dataUrlType: item.interaction.data_url_type,
      transcription: item.interaction.transcription,
      transcription_model: item.interaction.transcription_model,
      transcription_status: item.interaction.transcription_status,
      transcription_error: item.interaction.transcription_error,
      meetingBookedFromThis: item.interaction.meetingBookedFromThis,
      data: null,
      liConversationUrl: null,
      liConversationId: null,
      cadenceId: null,
      orgId: null,
      stepId: null,
      linkViewId: null,
      documentId: null,
      globalReminderId: null,
    },
    interactionUser: item.interactionUser
      ? {
          userId: '',
          teamId: '',
          teamName: '',
          teamPriceTier: '',
          name: item.interactionUser.name,
          profilePicture: item.interactionUser.profilePicture,
          title: null,
          calendarLink: null,
          contactNumber: null,
          email: item.interactionUser.email,
          liConnected: null,
          liConnectedAt: null,
          liReceivedConnection: null,
          liReceivedConnectionAt: null,
          liSentConnection: null,
          liSentConnectionAt: null,
          liFollowed: null,
          liFollowedAt: null,
          emailCredentialId: null,
          disqualified: false,
          disqualifiedAt: null,
          meetingInterested: false,
        }
      : null,
    documentView: item.documentView
      ? {
          id: item.documentView.doc_id || '',
          docId: item.documentView.doc_id,
          title: item.documentView.title || '',
          url: '',
          ogImageUrl: item.documentView.og_image_url,
          description: item.documentView.description,
          fp_ip_loc_city_name: item.documentView.fp_ip_loc_city_name,
          fp_ip_loc_country_name: item.documentView.fp_ip_loc_country_name,
          fp_browser_name: item.documentView.fp_browser_name,
          fp_os: item.documentView.fp_os,
        }
      : null,
    contact: {
      id: '',
      teamId: '',
      orgId: null,
      stageId: null,
      tracked: true,
      lastEnrichedAt: null,
      unverified: false,
      notes: null,
      liId: null,
      liUsername: null,
      liSalesNavId: null,
      liHiring: null,
      liOpenToWork: null,
      liOpenLink: null,
      liPremium: null,
      liAbout: null,
      liAboutSummary: null,
      liConnectionsCount: null,
      liRecentPostsCount: null,
      liActiveUserProbability: null,
      fields: {},
      primaryFields: {
        firstName: {
          createdAt: new Date(),
          fieldName: 'firstName',
          source: '',
          data: item.contact.first_name || '',
          enrichSource: null,
          primary: true,
          verified: null,
          emailVerificationCode: null,
          confidenceScore: null,
        },
        lastName: {
          createdAt: new Date(),
          fieldName: 'lastName',
          source: '',
          data: item.contact.last_name || '',
          enrichSource: null,
          primary: true,
          verified: null,
          emailVerificationCode: null,
          confidenceScore: null,
        },
      },
      primaryPictureUrl: item.contact.picture_url,
      ageEstimate: null,
    },
    cadence: item.cadence
      ? {
          id: item.cadence.id ?? '',
          teamId: '',
          userId: '',
          contactId: '',
          cadenceTemplateId: '',
          name: item.cadence.name,
          paused: false,
          deletedAt: null,
          createdAt: new Date(),
          startTime: null,
        }
      : null,
  }));
}
